import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Copy, Check, Download, Expand, X, Loader2, Zap, Bot, Globe, FileText, Image as ImageIcon, ExternalLink, Clock, Code, Eye, Maximize2 } from 'lucide-react';
import { useState } from 'react';
import WebSearchProgress from './WebSearchProgress';
import ThinkingDisplay from './ThinkingDisplay';
import AttachmentDisplay from './AttachmentDisplay';
import CitationRenderer from './CitationRenderer';
import { useAppearance } from '../contexts/AppearanceContext';

// Utility function to process web search tokens in static content and create content segments
const processWebSearchTokens = (content) => {
  if (!content || typeof content !== 'string') return { contentSegments: [{ type: 'content', content }] };
  
  const contentSegments = [];
  let workingContent = content;
  
  // Find all web search sections and their positions
  // The format in database: WEB_SEARCH_QUERY__query__WEB_SEARCH_RESULTS_START____WEB_SEARCH_RESULT__{...}
  // We need to capture results more precisely to avoid consuming remaining content
  const webSearchRegex = /WEB_SEARCH_QUERY__(.*?)__WEB_SEARCH_RESULTS_START__((?:_?_?WEB_SEARCH_RESULT__\{[^}]+\})*)/gs;
  const webSearchMatches = [...workingContent.matchAll(webSearchRegex)];
  
  if (webSearchMatches.length === 0) {
    // No web search sections found, return content as-is
    return { contentSegments: [{ type: 'content', content: workingContent }] };
  }
  
  // Sort matches by position
  webSearchMatches.sort((a, b) => a.index - b.index);
  
  let lastIndex = 0;
  
  webSearchMatches.forEach((match, index) => {
    // Add content before this web search section
    if (match.index > lastIndex) {
      const beforeContent = workingContent.substring(lastIndex, match.index);
      if (beforeContent.trim()) {
        contentSegments.push({
          type: 'content',
          content: beforeContent
        });
      }
    }
    
    // Extract query and results from the match
    const query = match[1] ? match[1].trim() : '';
    const resultsSection = match[2];
    
    // Extract results from the results section
    // Results are in format: __WEB_SEARCH_RESULT__{"..."}__WEB_SEARCH_RESULT__{"..."} 
    const resultPattern = /__WEB_SEARCH_RESULT__(\{[^}]+\})/g;
    const results = [];
    
    let resultMatch;
    while ((resultMatch = resultPattern.exec(resultsSection)) !== null) {
      try {
        const result = JSON.parse(resultMatch[1]);
        results.push(result);
      } catch (e) {
        console.error('Failed to parse web search result:', e, resultMatch[1]);
      }
    }
    
    // Add web search segment
    if (results.length > 0) {
      contentSegments.push({
        type: 'web_search',
        id: `search-${index}`,
        query,
        results,
        status: 'results'
      });
    }
    
    lastIndex = match.index + match[0].length;
  });
  
  // Add any remaining content after the last web search section
  if (lastIndex < workingContent.length) {
    const remainingContent = workingContent.substring(lastIndex);
    
    // Clean up tokens from remaining content
    let cleanedRemaining = remainingContent;
    const cleanupPatterns = [
      /WEB_SEARCH_QUERY__.*?__WEB_SEARCH_RESULTS_START__.*?(?=WEB_SEARCH_QUERY__|$)/gs,
      /WEB_SEARCH_START__/g,
      /WEB_SEARCH_EXECUTING__/g,
      /WEB_SEARCH_RESULTS_START__/g,
      /_?_?WEB_SEARCH_RESULT__\{[^}]*\}/gm
    ];
    
    cleanupPatterns.forEach(pattern => {
      cleanedRemaining = cleanedRemaining.replace(pattern, '');
    });
    
    cleanedRemaining = cleanedRemaining.replace(/\n\s*\n/g, '\n\n').trim();
    
    if (cleanedRemaining) {
      contentSegments.push({
        type: 'content',
        content: cleanedRemaining
      });
    }
  }
  
  return { contentSegments };
};

const ProgressDisplay = ({ status, text }) => {
  if (!status || !text) return null;

  const getProgressConfig = (status) => {
    switch (status) {
      case 'starting':
        return {
          icon: Bot,
          bgColor: 'bg-blue-50',
          iconColor: 'text-blue-600',
          textColor: 'text-blue-800',
          borderColor: 'border-blue-200'
        };
      case 'tool_auto':
        return {
          icon: Zap,
          bgColor: 'bg-purple-50',
          iconColor: 'text-purple-600',
          textColor: 'text-purple-800',
          borderColor: 'border-purple-200'
        };
      case 'streaming':
        return {
          icon: Loader2,
          bgColor: 'bg-green-50',
          iconColor: 'text-green-600',
          textColor: 'text-green-800',
          borderColor: 'border-green-200'
        };
      case 'tools_ready':
        return {
          icon: Globe,
          bgColor: 'bg-orange-50',
          iconColor: 'text-orange-600',
          textColor: 'text-orange-800',
          borderColor: 'border-orange-200'
        };
      default:
        return {
          icon: Bot,
          bgColor: 'bg-gray-50',
          iconColor: 'text-gray-600',
          textColor: 'text-gray-800',
          borderColor: 'border-gray-200'
        };
    }
  };

  const config = getProgressConfig(status);
  const Icon = config.icon;

  return (
    <div className={`flex items-center gap-3 p-3 mb-3 rounded-lg border ${config.bgColor} ${config.borderColor} transition-all duration-300`}>
      <Icon 
        className={`w-4 h-4 ${config.iconColor} ${status === 'streaming' ? 'animate-spin' : ''}`} 
      />
      <span className={`text-sm font-medium ${config.textColor}`}>
        {text}
      </span>
    </div>
  );
};

const CodeBlock = ({ children, className, ...props }) => {
  const [copied, setCopied] = useState(false);
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  if (match) {
    return (
      <div className="relative group">
        <button
          onClick={handleCopy}
          className="absolute top-2 right-2 p-2 rounded bg-gray-700 hover:bg-gray-600 opacity-0 group-hover:opacity-100 transition-opacity z-10"
          title="Copy code"
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-400" />
          ) : (
            <Copy className="w-4 h-4 text-gray-300" />
          )}
        </button>
        <SyntaxHighlighter
          style={oneDark}
          language={language}
          PreTag="div"
          className="rounded-lg"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      </div>
    );
  }

  return (
    <code className="bg-gray-100 dark:bg-gray-700 text-claude-text px-1 py-0.5 rounded text-sm font-mono" {...props}>
      {children}
    </code>
  );
};


const MessageRenderer = React.memo(({ content, isStreaming = false, webSearchActive, webSearchStatus, webSearchQuery, thinkingContent, isThinking, showProgress = false, progressStatus = null, progressText = null, attachments = [], onCitationClick, contextSpaceId }) => {
  const [modalImage, setModalImage] = useState(null);
  const [htmlViewMode, setHtmlViewMode] = useState('preview'); // 'preview' or 'code'
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { isSuppleDark } = useAppearance();

  // Function to detect if content contains HTML code blocks
  const isHtmlContent = (text) => {
    if (!text || typeof text !== 'string') return false;
    
    // Look for ```html code blocks
    const htmlCodeBlockPattern = /```html\s*([\s\S]*?)```/i;
    return htmlCodeBlockPattern.test(text);
  };

  // Extract HTML content from code blocks
  const extractHtmlFromCodeBlock = (text) => {
    const htmlCodeBlockPattern = /```html\s*([\s\S]*?)```/i;
    const match = text.match(htmlCodeBlockPattern);
    if (match) {
      // Unescape any HTML entities that might have been escaped during storage
      return match[1].trim()
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
    }
    return text;
  };
  
  // Process web search tokens in static content and create content segments
  const { contentSegments } = processWebSearchTokens(content);
  
  // Simplified context-aware preprocessing with better table detection
  const preprocessContent = (text) => {
    if (!text) return '';
    
    // First, escape potential XML-like tags that might cause React warnings
    // This handles tags like <webfetch>, <websearch>, <query>, <url> etc.
    let processedText = text.replace(/<(\/?)(\w+)>/g, (match, slash, tag) => {
      // List of known HTML tags that should not be escaped
      const validHtmlTags = ['p', 'br', 'div', 'span', 'strong', 'em', 'code', 'pre', 'ul', 'ol', 'li', 'a', 'img', 
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'tr', 'td', 'th', 'thead', 'tbody', 'tfoot',
        'html', 'head', 'body', 'title', 'meta', 'link', 'style', 'script', 'header', 'footer', 
        'nav', 'main', 'section', 'article', 'aside', 'form', 'input', 'button', 'label', 'select', 
        'option', 'textarea', 'iframe', 'video', 'audio', 'canvas', 'svg', 'path'];
      
      if (validHtmlTags.includes(tag.toLowerCase())) {
        return match; // Keep valid HTML tags
      } else {
        return `&lt;${slash}${tag}&gt;`; // Escape unknown tags
      }
    });
    
    // Check if text contains markdown table patterns
    const hasTable = /\|.*\|/.test(processedText) && /\|[\s\-:]*\|/.test(processedText);
    
    // If it contains tables, convert problematic newlines to <br> tags
    if (hasTable) {
      // Simple approach: convert newlines that appear to continue list items or content
      return processedText.replace(/\n(?=- )/g, '<br>');
    }
    
    // Find protected blocks: code blocks, inline code, and quotes
    const protectedBlocks = [];
    const blockCodeRegex = /```[\s\S]*?```/g;
    const inlineCodeRegex = /`[^`\n]*?`/g;
    const quoteRegex = /^>.*$/gm;
    
    let match;
    
    // Find block code
    while ((match = blockCodeRegex.exec(processedText)) !== null) {
      protectedBlocks.push({ start: match.index, end: match.index + match[0].length });
    }
    
    // Find inline code
    blockCodeRegex.lastIndex = 0;
    while ((match = inlineCodeRegex.exec(processedText)) !== null) {
      protectedBlocks.push({ start: match.index, end: match.index + match[0].length });
    }
    
    // Find quotes
    inlineCodeRegex.lastIndex = 0;
    while ((match = quoteRegex.exec(processedText)) !== null) {
      protectedBlocks.push({ start: match.index, end: match.index + match[0].length });
    }
    
    // Sort by position
    protectedBlocks.sort((a, b) => a.start - b.start);
    
    // Helper function to check if a position is inside a protected block
    const isInProtectedBlock = (position) => {
      return protectedBlocks.some(block => position >= block.start && position < block.end);
    };
    
    // Process HTML tags only outside protected blocks
    let result = processedText;
    const htmlTagRegex = /<(br\s*\/?|p|\/p)>/gi;
    const matches = [];
    
    // Collect all HTML tag matches with their positions
    let tagMatch;
    while ((tagMatch = htmlTagRegex.exec(processedText)) !== null) {
      matches.push({
        match: tagMatch[0],
        tag: tagMatch[1],
        start: tagMatch.index,
        end: tagMatch.index + tagMatch[0].length
      });
    }
    
    // Process matches in reverse order to maintain correct positions
    matches.reverse().forEach(({ match, tag, start, end }) => {
      if (!isInProtectedBlock(start)) {
        const before = result.substring(0, start);
        const after = result.substring(end);
        
        let replacement = '';
        if (tag.toLowerCase().startsWith('br')) {
          replacement = '  \n'; // Markdown line break
        } else if (tag.toLowerCase() === 'p') {
          replacement = ''; // Remove opening <p>
        } else if (tag.toLowerCase() === '/p') {
          replacement = '\n\n'; // Convert closing </p> to double line break
        }
        
        result = before + replacement + after;
      }
    });
    
    return result;
  };
  
  
  const downloadImage = (src, filename = 'generated-image.png') => {
    const link = document.createElement('a');
    link.href = src;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const components = {
    code: CodeBlock,
    pre: ({ children }) => <div>{children}</div>,
    h1: ({ children }) => <h1 className="text-2xl font-bold mt-6 mb-4 text-claude-text">{children}</h1>,
    h2: ({ children }) => <h2 className="text-xl font-bold mt-5 mb-3 text-claude-text">{children}</h2>,
    h3: ({ children }) => <h3 className="text-lg font-semibold mt-4 mb-2 text-claude-text">{children}</h3>,
    h4: ({ children }) => <h4 className="text-base font-semibold mt-3 mb-2 text-claude-text">{children}</h4>,
    h5: ({ children }) => <h5 className="text-sm font-semibold mt-2 mb-2 text-claude-text">{children}</h5>,
    h6: ({ children }) => <h6 className="text-xs font-semibold mt-2 mb-1 text-claude-text">{children}</h6>,
    p: ({ children }) => {
      // Handle citations in content
      const textContent = typeof children === 'string' ? children : 
        Array.isArray(children) ? children.join('') :
        React.isValidElement(children) ? React.Children.toArray(children).map(child => 
          typeof child === 'string' ? child : child?.props?.children || ''
        ).join('') : String(children || '');
      
      if (typeof textContent === 'string' && textContent.includes('[Source:')) {
        return (
          <div className="mb-4 leading-relaxed text-claude-text">
            <CitationRenderer 
              content={textContent} 
              onCitationClick={onCitationClick}
              contextSpaceId={contextSpaceId}
            />
          </div>
        );
      }
      return <p className="mb-4 leading-relaxed text-claude-text">{children}</p>;
    },
    ul: ({ children }) => <ul className="list-disc ml-6 mb-4 space-y-1">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal ml-6 mb-4 space-y-1">{children}</ol>,
    li: ({ children }) => <li className="text-claude-text leading-relaxed">{children}</li>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-claude-accent pl-4 italic text-claude-muted my-4 bg-gray-50 dark:bg-gray-800 py-2 rounded-r">
        {children}
      </blockquote>
    ),
    a: ({ href, children }) => (
      <a 
        href={href} 
        className="text-claude-accent hover:underline break-words" 
        target="_blank" 
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    table: ({ children }) => (
      <div className="overflow-x-auto my-4 max-w-full">
        <table className="border border-gray-300 dark:border-gray-700 border-collapse bg-gray-50 dark:bg-gray-900/50 rounded-lg shadow-sm" style={{ tableLayout: 'auto', width: 'max-content', minWidth: '100%' }}>
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-gray-100 dark:bg-gray-800/70 border-b-2 border-gray-300 dark:border-gray-700">
        {children}
      </thead>
    ),
    tbody: ({ children }) => (
      <tbody className="divide-y divide-gray-200 dark:divide-gray-700/50">
        {children}
      </tbody>
    ),
    tr: ({ children }) => (
      <tr className="bg-white dark:bg-gray-900/30 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
        {children}
      </tr>
    ),
    th: ({ children }) => (
      <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-50">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-700 dark:text-gray-100">
        {children}
      </td>
    ),
    hr: () => <hr className="my-6 border-claude-border" />,
    br: () => <br />,
    strong: ({ children }) => <strong className="font-semibold text-claude-text">{children}</strong>,
    em: ({ children }) => <em className="italic text-claude-text">{children}</em>,
    del: ({ children }) => <del className="line-through text-claude-muted">{children}</del>,
    details: ({ children }) => (
      <details className="my-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        {children}
      </details>
    ),
    summary: ({ children }) => (
      <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900 select-none">
        {children}
      </summary>
    ),
    img: ({ src, alt }) => {
      // Handle base64 images
      if (src && src.startsWith('data:image')) {
        return (
          <div className="my-4">
            <img 
              src={src} 
              alt={alt || 'Generated image'} 
              className="max-w-full h-auto rounded-lg shadow-lg"
              style={{ maxHeight: '600px' }}
            />
          </div>
        );
      }
      // Handle regular images
      return (
        <div className="my-4">
          <img 
            src={src} 
            alt={alt || 'Image'} 
            className="max-w-full h-auto rounded-lg shadow-lg"
            style={{ maxHeight: '600px' }}
          />
        </div>
      );
    },
  };

  // For streaming messages, we handle tool displays differently
  const shouldShowToolsAbove = !isStreaming;

  // Create unified segments that properly interleave content, web search, and HTML
  const createUnifiedSegments = () => {
    // If no web search tokens, just process HTML blocks
    if (!content.includes('WEB_SEARCH')) {
      const segments = [];
      if (isHtmlContent(content)) {
        let remainingContent = content;
        const htmlBlockRegex = /```html\s*([\s\S]*?)```/i;
        
        while (remainingContent) {
          const match = remainingContent.match(htmlBlockRegex);
          
          if (match) {
            // Add content before the HTML block
            if (match.index > 0) {
              const beforeContent = remainingContent.substring(0, match.index).trim();
              if (beforeContent) {
                segments.push({ type: 'content', content: beforeContent });
              }
            }
            
            // Add the HTML content (unescaped)
            const htmlContent = match[1].trim()
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&amp;/g, '&')
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'");
            segments.push({ type: 'html_content', content: htmlContent });
            
            // Continue with remaining content after the HTML block
            remainingContent = remainingContent.substring(match.index + match[0].length);
          } else {
            // No more HTML blocks, add remaining content
            if (remainingContent.trim()) {
              segments.push({ type: 'content', content: remainingContent.trim() });
            }
            break;
          }
        }
      } else {
        segments.push({ type: 'content', content });
      }
      return segments;
    }
    
    // Process content with web search tokens
    const { contentSegments: webSearchSegments } = processWebSearchTokens(content);
    
    // If content has HTML blocks, we need to extract HTML separately
    if (isHtmlContent(content)) {
      // The webSearchSegments already has content segments with web search removed
      // Now we need to process those content segments to extract HTML blocks
      const finalSegments = [];
      
      webSearchSegments.forEach(segment => {
        if (segment.type === 'web_search') {
          // Keep web search segments as-is
          finalSegments.push(segment);
        } else if (segment.type === 'content') {
          // Process content segments to extract HTML blocks
          let remainingContent = segment.content;
          const htmlBlockRegex = /```html\s*([\s\S]*?)```/i;
          
          while (remainingContent) {
            const match = remainingContent.match(htmlBlockRegex);
            
            if (match) {
              // Add content before the HTML block
              if (match.index > 0) {
                const beforeContent = remainingContent.substring(0, match.index).trim();
                if (beforeContent) {
                  finalSegments.push({ type: 'content', content: beforeContent });
                }
              }
              
              // Add the HTML content (unescaped)
              const htmlContent = match[1].trim()
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&amp;/g, '&')
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'");
              finalSegments.push({ type: 'html_content', content: htmlContent });
              
              // Continue with remaining content after the HTML block
              remainingContent = remainingContent.substring(match.index + match[0].length);
            } else {
              // No more HTML blocks, add remaining content
              if (remainingContent.trim()) {
                finalSegments.push({ type: 'content', content: remainingContent.trim() });
              }
              break;
            }
          }
        }
      });
      
      return finalSegments;
    }
    
    // No HTML blocks, return web search segments as-is
    return webSearchSegments;
  };
  
  const processedSegments = createUnifiedSegments();

  return (
    <>
      {shouldShowToolsAbove && thinkingContent && (
        <ThinkingDisplay content={thinkingContent} isActive={isThinking} />
      )}
      {shouldShowToolsAbove && webSearchActive && (
        <WebSearchProgress status={webSearchStatus} query={webSearchQuery} />
      )}
      {shouldShowToolsAbove && showProgress && (
        <ProgressDisplay status={progressStatus} text={progressText} />
      )}
      <AttachmentDisplay attachments={attachments} onImageClick={setModalImage} />
      <div className="message-content prose prose-sm max-w-none">
        {/* Render all segments in order */}
        {processedSegments.map((segment, index) => {
            if (segment.type === 'html_content') {
              // Render HTML content with preview functionality
              return (
                <div key={index} className="my-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700">
                    {/* HTML View Header */}
                    <div className="flex items-center justify-between p-4 border-b bg-gray-50 dark:bg-gray-700 rounded-t-lg">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">HTML Report</h3>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* View Mode Switcher */}
                        <div className="flex items-center bg-gray-200 dark:bg-gray-600 rounded-lg p-0.5">
                          <button
                            onClick={() => setHtmlViewMode('preview')}
                            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                              htmlViewMode === 'preview'
                                ? 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 shadow-sm'
                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                            }`}
                          >
                            <Eye className="w-3 h-3" />
                            Preview
                          </button>
                          <button
                            onClick={() => setHtmlViewMode('code')}
                            className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                              htmlViewMode === 'code'
                                ? 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 shadow-sm'
                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                            }`}
                          >
                            <Code className="w-3 h-3" />
                            Code
                          </button>
                        </div>
                        
                        {/* Fullscreen and Download buttons (only in preview mode) */}
                        {htmlViewMode === 'preview' && (
                          <>
                            <button
                              onClick={() => setIsFullscreen(!isFullscreen)}
                              className="p-1 text-gray-600 hover:text-gray-900 rounded hover:bg-gray-200 transition-colors"
                              title="Fullscreen"
                            >
                              <Maximize2 className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => {
                                // Create a new window with the HTML content for printing to PDF
                                const printWindow = window.open('', '_blank');
                                printWindow.document.write(`
                                  <!DOCTYPE html>
                                  <html>
                                  <head>
                                    <title>Report - ${new Date().toISOString().split('T')[0]}</title>
                                    <style>
                                      @media print {
                                        body { margin: 0; }
                                        @page { margin: 1in; }
                                      }
                                    </style>
                                  </head>
                                  <body>
                                    ${segment.content}
                                  </body>
                                  </html>
                                `);
                                printWindow.document.close();
                                
                                // Wait for content to load, then trigger print dialog
                                printWindow.onload = () => {
                                  setTimeout(() => {
                                    printWindow.print();
                                    printWindow.close();
                                  }, 500);
                                };
                              }}
                              className="p-1 text-gray-600 hover:text-gray-900 rounded hover:bg-gray-200 transition-colors"
                              title="Download as PDF"
                            >
                              <Download className="w-3 h-3" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* HTML Content */}
                    <div className={isFullscreen ? 'fixed inset-0 z-50 bg-white overflow-auto' : 'max-h-[600px] overflow-hidden'}>
                      {isFullscreen && (
                        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
                          <h3 className="text-lg font-semibold">HTML Report - Fullscreen</h3>
                          <button
                            onClick={() => setIsFullscreen(false)}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                      
                      {htmlViewMode === 'preview' ? (
                        <div className={isFullscreen ? 'p-6' : 'p-4'}>
                          <div className="relative w-full overflow-hidden" style={{ height: isFullscreen ? 'calc(100vh - 120px)' : '500px' }}>
                            <iframe
                              srcDoc={`
                                <!DOCTYPE html>
                                <html>
                                <head>
                                  <meta charset="UTF-8">
                                  <meta name="viewport" content="width=device-width, initial-scale=1.0">
                                  <style>
                                    body { 
                                      margin: 0; 
                                      padding: 16px; 
                                      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                                      overflow-x: auto;
                                    }
                                    * { box-sizing: border-box; }
                                  </style>
                                </head>
                                <body>
                                  ${segment.content}
                                </body>
                                </html>
                              `}
                              className="absolute inset-0 w-full h-full border-0 bg-white rounded"
                              title="HTML Preview"
                              sandbox="allow-scripts"
                              style={{ 
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                                isolation: 'isolate'
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">HTML Source</span>
                            <button
                              onClick={() => navigator.clipboard.writeText(segment.content)}
                              className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 font-medium"
                            >
                              <Copy className="w-3 h-3" />
                              Copy
                            </button>
                          </div>
                          <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-auto max-h-[500px] font-mono">
                            <code>{segment.content}</code>
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            } else if (segment.type === 'web_search') {
              return (
                <div key={`${segment.id || index}`} className="my-4">
                  <WebSearchProgress 
                    status={segment.status}
                    query={segment.query}
                    results={segment.results}
                  />
                </div>
              );
            } else if (segment.type === 'content') {
              // Regular content - render with markdown
              const processedContent = preprocessContent(segment.content);
            
            // Check if content contains base64 images and extract them
            const imageRegex = /!\[([^\]]*)\]\((data:image\/[^;]+;base64,[^)]+)\)/g;
            const parts = [];
            let lastIndex = 0;
            let match;
            
            while ((match = imageRegex.exec(processedContent)) !== null) {
              // Add text before the image
              if (match.index > lastIndex) {
                parts.push({
                  type: 'text',
                  content: processedContent.substring(lastIndex, match.index)
                });
              }
              
              // Add the image
              parts.push({
                type: 'image',
                alt: match[1],
                src: match[2]
              });
              
              lastIndex = match.index + match[0].length;
            }
            
            // Add any remaining text
            if (lastIndex < processedContent.length) {
              parts.push({
                type: 'text',
                content: processedContent.substring(lastIndex)
              });
            }
            
            // If no images found, just use the processed content
            if (parts.length === 0) {
              parts.push({
                type: 'text',
                content: processedContent
              });
            }
            
            return (
              <div key={index}>
                {parts.map((part, partIndex) => {
                  if (part.type === 'image') {
                    return (
                      <div key={partIndex} className="my-4 relative group">
                        <img 
                          src={part.src} 
                          alt={part.alt || 'Generated image'} 
                          className="max-w-full h-auto rounded-lg shadow-lg cursor-pointer"
                          style={{ maxHeight: '600px' }}
                          onClick={() => setModalImage(part.src)}
                          onError={(e) => {
                            console.error('Image failed to load:', e);
                            e.target.style.display = 'none';
                          }}
                        />
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setModalImage(part.src);
                            }}
                            className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100 transition-colors"
                            title="View full size"
                          >
                            <Expand className="w-4 h-4 text-gray-700" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadImage(part.src, `supple-${Date.now()}.png`);
                            }}
                            className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100 transition-colors"
                            title="Download image"
                          >
                            <Download className="w-4 h-4 text-gray-700" />
                          </button>
                        </div>
                      </div>
                    );
                  } else {
                    // Check if this part contains citations and process them first
                    if (part.content && part.content.includes('[Source:')) {
                      return (
                        <div key={partIndex} className="mb-4">
                          <CitationRenderer 
                            content={part.content} 
                            onCitationClick={onCitationClick}
                            contextSpaceId={contextSpaceId}
                          />
                        </div>
                      );
                    } else {
                      return (
                        <ReactMarkdown
                          key={partIndex}
                          components={components}
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                          skipHtml={false}
                        >
                          {part.content}
                        </ReactMarkdown>
                      );
                    }
                  }
                })}
              </div>
            );
            }
          })}
        {isStreaming && (
          <span className="inline-block w-2 h-5 bg-claude-accent animate-pulse ml-1"></span>
        )}
      </div>
      
      {/* Full screen modal */}
      {modalImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
          onClick={() => setModalImage(null)}
        >
          <div className="relative max-w-full max-h-full">
            <img 
              src={modalImage} 
              alt="Full size" 
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setModalImage(null)}
              className="absolute top-4 right-4 p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100 transition-colors"
              title="Close"
            >
              <X className="w-6 h-6 text-gray-700" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                downloadImage(modalImage, `supple-${Date.now()}.png`);
              }}
              className="absolute bottom-4 right-4 p-3 bg-white rounded-lg shadow-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
              title="Download image"
            >
              <Download className="w-5 h-5 text-gray-700" />
              <span className="text-gray-700 font-medium">Download</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
});

export default MessageRenderer;